import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { loadOrdersAsync } from '../../../redux/feathers/ordersSlice';
import LoadingSpinner from '../../SharedComponents/LoadingSpinner/LoadingSpinner';
import styles from './MyOrders.module.css';

const MyOrders = () => {
  const dispatch = useDispatch();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    dispatch(loadOrdersAsync({ page: currentPage, size: pageSize }));
  }, [dispatch, currentPage]);

  const orders = useSelector((state) => state.orders);
  const { ordersState, status, pagination } = orders;

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const calculateOrderTotal = (order) => {
    if (!order?.products) return 0;
    return order.products.reduce((total, product) => {
      const productTotal = product.packagingOptions?.reduce((sum, pkg) => {
        return sum + (pkg.price * pkg.quantityOrder);
      }, 0) || 0;
      return total + productTotal;
    }, 0);
  };

  const renderPagination = () => {
    const totalPages = pagination.totalPages;
    const currentPageNum = pagination.number;

    if (totalPages <= 1) return null;

    const pages = [];

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => setCurrentPage(currentPageNum - 1)}
        disabled={currentPageNum === 0}
        className={styles.pagination__btn}
      >
        Previous
      </button>
    );

    // First page
    if (totalPages > 0) {
      pages.push(
        <button
          key={0}
          onClick={() => setCurrentPage(0)}
          className={`${styles.pagination__btn} ${currentPageNum === 0 ? styles.active : ''}`}
        >
          1
        </button>
      );
    }

    // Ellipsis after first page
    if (currentPageNum > 2) {
      pages.push(<span key="ellipsis1" className={styles.pagination__ellipsis}>...</span>);
    }

    // Middle pages
    const startPage = Math.max(1, currentPageNum - 1);
    const endPage = Math.min(totalPages - 2, currentPageNum + 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`${styles.pagination__btn} ${currentPageNum === i ? styles.active : ''}`}
        >
          {i + 1}
        </button>
      );
    }

    // Ellipsis before last page
    if (currentPageNum < totalPages - 3) {
      pages.push(<span key="ellipsis2" className={styles.pagination__ellipsis}>...</span>);
    }

    // Last page
    if (totalPages > 1) {
      pages.push(
        <button
          key={totalPages - 1}
          onClick={() => setCurrentPage(totalPages - 1)}
          className={`${styles.pagination__btn} ${currentPageNum === totalPages - 1 ? styles.active : ''}`}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => setCurrentPage(currentPageNum + 1)}
        disabled={currentPageNum === totalPages - 1}
        className={styles.pagination__btn}
      >
        Next
      </button>
    );

    return <div className={styles.pagination}>{pages}</div>;
  };

  return (
    <section id={styles.my__order}>
      <h1>My Orders</h1>
      {status === 'Pending' ? (
        <LoadingSpinner />
      ) : (
        <>
          {!ordersState.length ? (
            <div className={styles.placeholder__text}>
              <span className={styles.placeholder__image}>
                <svg stroke='currentColor' fill='#027333' strokeWidth='0' viewBox='0 0 512 512' height='30px' width='30px' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M454.65 169.4A31.82 31.82 0 00432 160h-64v-16a112 112 0 00-224 0v16H80a32 32 0 00-32 32v216c0 39 33 72 72 72h272a72.22 72.22 0 0050.48-20.55 69.48 69.48 0 0021.52-50.2V192a31.75 31.75 0 00-9.35-22.6zM176 144a80 80 0 01160 0v16H176zm192 96a112 112 0 01-224 0v-16a16 16 0 0132 0v16a80 80 0 00160 0v-16a16 16 0 0132 0z'></path>
                </svg>
              </span>
              <h6>You Haven't Ordered Any Product</h6>
              <p>No items added in your Order List. Start shopping to create your first order!</p>
            </div>
          ) : (
            <>
              <Table bordered size='sm' responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Reference</th>
                    <th>Order Date</th>
                    <th>Shipping Method</th>
                    <th>City</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ordersState.map((order, idx) => {
                    const orderId = order.id || order._id;
                    const orderTotal = calculateOrderTotal(order);
                    const orderNumber = currentPage * pageSize + idx + 1;

                    return (
                      <tr key={orderId}>
                        <td className='fw-bold'>{orderNumber}</td>
                        <td>{order.sequence || 'N/A'}</td>
                        <td>{formatDate(order.createdAt || order.orderTime)}</td>
                        <td>{order.shippingMethod || 'N/A'}</td>
                        <td>{order.shippingAddress?.city || 'N/A'}</td>
                        <td>
                          <span className={styles[`status__${order.status?.toLowerCase()}`]}>
                            {order.status || 'PENDING'}
                          </span>
                        </td>
                        <td className='fw-bold'>${orderTotal.toFixed(2)}</td>
                        <td>
                          <span onClick={() => handleViewDetails(order)} className={styles.action__icon}>
                            <FontAwesomeIcon icon={faEye} className={styles.view__icon} title="View Details" />
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          )}
        </>
      )}

      {/* Order Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <div className={styles.order__details}>
              <div className={styles.order__info}>
                <h5>Order Information</h5>
                <p><strong>Reference:</strong> {selectedOrder.sequence || 'N/A'}</p>
                <p><strong>Order Date:</strong> {formatDate(selectedOrder.createdAt || selectedOrder.orderTime)}</p>
                <p><strong>Status:</strong> <span className={styles[`status__${selectedOrder.status?.toLowerCase()}`]}>{selectedOrder.status}</span></p>
                <p><strong>Shipping Method:</strong> {selectedOrder.shippingMethod || 'N/A'}</p>
                <p><strong>Shipping Fee:</strong> ${selectedOrder.shippingFee || 0}</p>
              </div>

              <div className={styles.shipping__info}>
                <h5>Shipping Address</h5>
                {selectedOrder.shippingAddress ? (
                  <>
                    <p>{selectedOrder.shippingAddress.line1}</p>
                    {selectedOrder.shippingAddress.line2 && <p>{selectedOrder.shippingAddress.line2}</p>}
                    <p>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state} {selectedOrder.shippingAddress.postalCode}</p>
                    <p>{selectedOrder.shippingAddress.country}</p>
                  </>
                ) : (
                  <p>No shipping address available</p>
                )}
              </div>

              <div className={styles.products__info}>
                <h5>Products</h5>
                {selectedOrder.products && selectedOrder.products.length > 0 ? (
                  <Table bordered size='sm'>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Packaging</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.products.map((product, idx) => {
                        return product.packagingOptions?.map((pkg, pkgIdx) => (
                          <tr key={`${idx}-${pkgIdx}`}>
                            <td>{product.name}</td>
                            <td>{pkg.unit}</td>
                            <td>{pkg.quantityOrder}</td>
                            <td>${pkg.price?.toFixed(2)}</td>
                            <td>${(pkg.price * pkg.quantityOrder).toFixed(2)}</td>
                          </tr>
                        ));
                      })}
                      <tr className={styles.total__row}>
                        <td colSpan="4" className="text-end"><strong>Total:</strong></td>
                        <td><strong>${calculateOrderTotal(selectedOrder).toFixed(2)}</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <p>No products in this order</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default MyOrders;
